import { hueOf, shadeFor } from "@wmf/shared/tutorColours";
import type { CSSProperties } from "react";
import type { Booking, BookingList, ColleagueLesson } from "../api.js";

/**
 * The one list the calendar draws, and who is allowed to be in it.
 *
 * The server sends two lists: the lessons this account may read, and
 * colleagues' lessons named only by who is teaching them. Two lists is the
 * right shape on the wire — it is what makes a colleague's child structurally
 * absent rather than nulled — but it is the wrong shape for drawing, because a
 * week is one grid and the boxes have to sort and overlap together.
 *
 * So they are joined here into a tagged union. `anonymous` is the tag, and it
 * is what every component asks before deciding whether to show a name or offer
 * a link. Nothing recomputes who owns what: the server already decided, and
 * this only carries the answer.
 */
export type DiaryEntry =
  | ({ anonymous: false } & Booking)
  | ({ anonymous: true } & ColleagueLesson);

/**
 * Statuses that mean there is no lesson to draw. Mirrors `isCancelledStatus`
 * in the booking provider — note the American spelling, which is what Amelia
 * sends and therefore what is stored. `waiting` is included because an Amelia
 * waiting-list place is not a confirmed lesson; the Hub-native waitlist is a
 * separate read model and is rendered in its own workspace.
 *
 * Only applied to lessons this account can read. Colleagues' non-lessons never
 * arrive: the server drops them, because a block nobody can identify is not
 * worth drawing for a lesson that is not happening.
 */
const NOT_HAPPENING = new Set(["canceled", "rejected", "waiting"]);

/** A missed booking or appointment still held its slot; it was not cancelled. */
export function isNoShow(entry: DiaryEntry | Booking): boolean {
  return ("bookingStatus" in entry && String(entry.bookingStatus ?? "").toLowerCase() === "no-show") ||
    ("status" in entry && String(entry.status ?? "").toLowerCase() === "no-show");
}

/** Partial attendance leaves the group lesson itself happening. */
export function isNoShowSession(session: { bookings: readonly DiaryEntry[] }): boolean {
  return session.bookings.length > 0 && session.bookings.every(isNoShow);
}

/** Everything to draw, in the order it happens. */
export function diaryEntries(
  data: BookingList | null,
  { includeNoShows = false }: { includeNoShows?: boolean } = {},
): DiaryEntry[] {
  const mine: DiaryEntry[] = (data?.bookings ?? [])
    .filter((booking) => !NOT_HAPPENING.has(booking.bookingStatus) &&
      (includeNoShows || !isNoShow(booking)))
    .map((booking) => ({ anonymous: false, ...booking }));

  const theirs: DiaryEntry[] = (data?.others ?? []).map((lesson) => ({
    anonymous: true,
    ...lesson,
  }));

  // Sorted here rather than relied upon from the API. A diary listing 19:30
  // above 17:00 is worse than useless, and the two lists arrive separately so
  // something has to interleave them anyway.
  return [...mine, ...theirs].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/**
 * Whose lessons are being shown.
 *
 * Either everybody, or a named set of tutors. `"all"` is a state of its own
 * rather than "every tutor currently ticked", and the difference matters when
 * you move weeks: the tutors on offer come from the window you are looking at,
 * so a colleague who only teaches next Tuesday is not in this week's list at
 * all. Ticked-all-of-this-week would quietly exclude them; `"all"` does not.
 */
export type Whose = "all" | ReadonlySet<string>;

/**
 * The diary's first scope for a signed-in member of staff.
 *
 * A tutor identity wins even when that account is also an owner: opening the
 * diary is a personal workspace first, and "Everyone" remains an explicit
 * choice. Accounts with no tutor profile have no honest "mine" scope, so they
 * fall back to the organisation view.
 */
export function defaultDiaryWhose(ownTutorId: string | null): Whose {
  return ownTutorId === null ? "all" : new Set([ownTutorId]);
}

/** How a tutor is offered in the filter. */
export interface TutorOption {
  /** The value the filter carries. `none` stands for "nobody recorded". */
  value: string;
  label: string;
}

/** The value standing for lessons whose tutor was never resolved. */
export const NO_TUTOR = "none";

/** Which box a lesson belongs to. */
function boxFor(entry: DiaryEntry): string {
  return entry.tutorId ?? NO_TUTOR;
}

/** The part of a name before the first space. "" for a name that is all space. */
function firstWord(name: string): string {
  const trimmed = name.trim();
  const space = trimmed.search(/\s/);
  return space === -1 ? trimmed : trimmed.slice(0, space);
}

/** "Matt Duffy" → "Matt D", for telling two Matts apart without the full name. */
function withInitial(name: string): string {
  const trimmed = name.trim();
  const first = firstWord(trimmed);
  const rest = trimmed.slice(first.length).trim();
  return rest === "" ? trimmed : `${first} ${rest[0]?.toUpperCase() ?? ""}`;
}

/**
 * First names for a row of tick boxes, lengthened only where they collide.
 *
 * A filter is read at a glance and surnames are what make it a paragraph, so
 * the default is the first name alone. What stops that being a bad idea is
 * that a school of eight tutors can easily hold two Matts, and a row offering
 * "Matt" twice is worse than one that never shortened anything: you cannot
 * tell which box you are ticking, and the calendar changes under you either
 * way.
 *
 * So a name is only shortened as far as it can go while staying unique in the
 * row. Two Matts become "Matt D" and "Matt S"; two Matt S's keep their names
 * in full. Only the ones that clash grow — one pair of Matts does not put
 * surnames back on everybody else.
 *
 * The row is what decides, which means a name can be shorter in one week than
 * in another. That is the honest behaviour for a filter built from the window
 * you are looking at: it is as short as it can be while still being unambiguous
 * about the names actually in front of you.
 */
export function shortenNames(names: readonly string[]): string[] {
  const labels = names.map((name) => firstWord(name) || name.trim());

  // Each pass lengthens only the labels that are still shared, so the cost of
  // a collision falls on the people who collide.
  for (const lengthen of [withInitial, (name: string) => name.trim()]) {
    const used = new Map<string, number>();
    for (const label of labels) used.set(label, (used.get(label) ?? 0) + 1);

    let changed = false;
    labels.forEach((label, index) => {
      if ((used.get(label) ?? 0) < 2) return;
      const longer = lengthen(names[index] ?? "");
      if (longer !== label) {
        labels[index] = longer;
        changed = true;
      }
    });

    if (!changed) break;
  }

  return labels;
}

/**
 * The tutors worth offering, taken from what is actually on screen.
 *
 * Built from the loaded window rather than from a list of staff, so the filter
 * never offers a name that would empty the calendar — and so it needs no
 * second request. The cost is that it changes as you move between weeks, which
 * is the honest behaviour: a colleague who teaches on Tuesdays is not a
 * useful thing to filter a Wednesday by.
 */
export function tutorsIn(entries: readonly DiaryEntry[]): TutorOption[] {
  const byId = new Map<string, string | null>();
  let unattributed = false;

  for (const entry of entries) {
    if (entry.tutorId === null) {
      unattributed = true;
      continue;
    }
    // First name wins; they are all the same tutor, and a later null would
    // otherwise overwrite a perfectly good name.
    if (!byId.has(entry.tutorId)) byId.set(entry.tutorId, entry.tutorName);
  }

  return tutorOptions(byId, unattributed);
}

/**
 * Turn a set of tutors into the row of names on offer.
 *
 * Separate from `tutorsIn` because the day view builds its row from two
 * sources — who is teaching, and who the rota says is in — and the shortening
 * has to see both at once. Run over one source and then the other, two Matts
 * arriving by different routes would both come out as "Matt".
 */
export function tutorOptions(
  byId: ReadonlyMap<string, string | null>,
  unattributed: boolean,
): TutorOption[] {
  const ids = [...byId.keys()];
  const labels = shortenNames(ids.map((id) => byId.get(id) ?? ""));

  const options = ids
    // A tutor profile with no display name is a data fault rather than
    // somebody called nothing, so it is described instead of shortened —
    // "Unnamed" on its own would read as a first name.
    .map((value, index) => ({ value, label: labels[index] || "Unnamed tutor" }))
    .sort((a, b) => a.label.localeCompare(b.label));

  if (unattributed) options.push({ value: NO_TUTOR, label: "No tutor recorded" });
  return options;
}

/**
 * Which tutor this account is, worked out from what it is allowed to read.
 *
 * The browser is never told its own tutor profile id — `/auth/me` does not
 * carry one — but it does not need to be: for anybody who is not an owner,
 * the lessons that came back named are exactly their own, so the tutor on one
 * of them is them. Null when they are teaching nothing in this window, which
 * is a real state and not an error.
 *
 * Meaningless for an owner, who reads every lesson named; callers ask only
 * when there is a distinction to draw.
 */
export function signedInTutorId(entries: readonly DiaryEntry[]): string | null {
  for (const entry of entries) if (!entry.anonymous) return entry.tutorId;
  return null;
}

/** Whether a tutor's box is ticked. */
export function isTicked(whose: Whose, value: string): boolean {
  return whose === "all" || whose.has(value);
}

/**
 * Whether the "everyone" box is ticked.
 *
 * True for `"all"`, and also when every tutor on offer happens to be ticked —
 * a master box that stayed empty while all its children were full would read
 * as broken.
 */
export function isEverybody(whose: Whose, tutors: readonly TutorOption[]): boolean {
  if (whose === "all") return true;
  return tutors.length > 0 && tutors.every((tutor) => whose.has(tutor.value));
}

/** Ticking "everyone" shows all of them; unticking it shows none. */
export function toggleAll(whose: Whose, tutors: readonly TutorOption[]): Whose {
  return isEverybody(whose, tutors) ? new Set<string>() : "all";
}

/**
 * Tick or untick one tutor.
 *
 * Unticking somebody while "everyone" is on turns it into the rest of them,
 * rather than doing nothing. Ticking the last outstanding one goes back to
 * `"all"`, so that a row of full boxes means what it looks like it means and
 * a tutor appearing in a later week is included rather than silently dropped.
 */
export function toggleTutor(
  whose: Whose,
  tutors: readonly TutorOption[],
  value: string,
): Whose {
  const next = new Set(whose === "all" ? tutors.map((tutor) => tutor.value) : whose);
  if (next.has(value)) next.delete(value);
  else next.add(value);

  if (tutors.length > 0 && tutors.every((tutor) => next.has(tutor.value))) return "all";
  return next;
}

/**
 * Narrow the calendar to whose lessons somebody asked for.
 *
 * Nothing here decides what may be *seen* — the server already did that, and a
 * colleague's lesson arrived without a child's name on it whatever this
 * returns. This only decides what is worth drawing.
 */
export function applyWhose(entries: readonly DiaryEntry[], whose: Whose): DiaryEntry[] {
  if (whose === "all") return [...entries];
  return entries.filter((entry) => whose.has(boxFor(entry)));
}

/**
 * The custom properties a lesson is drawn with, or nothing.
 *
 * Hue and saturation come from the tutor's stored colour and the shade from
 * what is being taught; how light the block ends up is the stylesheet's
 * business, because it is the only part that knows whether the page is in its
 * light or dark theme. A tutor nobody has given a colour returns undefined and
 * the block keeps the house accent, which is what every lesson looked like
 * before any of this.
 */
export function colourOf(entry: DiaryEntry): CSSProperties | undefined {
  const hsl = hueOf(entry.tutorColour);
  if (hsl === null) return undefined;

  return {
    "--hue": `${hsl.hue}`,
    "--sat": `${hsl.saturation}%`,
    "--shade": `${shadeFor(entry.instrument)}`,
  } as CSSProperties;
}

/** Whether this lesson has a colour of its own to be drawn in. */
export function hasColour(entry: DiaryEntry): boolean {
  return hueOf(entry.tutorColour) !== null;
}
